const MAX_RUNNER_TEXT_LENGTH = 160;
const STALE_AFTER_MS = 20 * 60 * 1000;

const MODEL_TIERS = {
  'gpt-5.6-luna': 'GPT-5.6 Luna',
  'gpt-5.6-terra': 'GPT-5.6 Terra',
  'gpt-5.6-sol': 'GPT-5.6 Sol',
};

export function isPlainObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

export function safeRunnerText(value, maxLength = MAX_RUNNER_TEXT_LENGTH) {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text && text.length <= maxLength ? text : null;
}

export function parseRunnerTimestamp(value) {
  const text = safeRunnerText(value);
  if (!text) return null;
  const timestamp = Date.parse(text);
  return Number.isFinite(timestamp) ? timestamp : null;
}

export function safeTimestampTitle(value) {
  return parseRunnerTimestamp(value) === null ? undefined : safeRunnerText(value);
}

export function safeRunnerNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

export function formatRunnerCount(value, placeholder = '—') {
  const count = safeRunnerNumber(value);
  return count === null ? placeholder : String(count);
}

export function formatRunnerDuration(value) {
  const milliseconds = safeRunnerNumber(value);
  if (milliseconds === null) return 'Not recorded';
  if (milliseconds === 0) return 'None queued';
  const minutes = Math.floor(milliseconds / 60000);
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h ${minutes % 60}m` : `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

export function formatRunnerAge(value, now = Date.now()) {
  const timestamp = parseRunnerTimestamp(value);
  if (timestamp === null) return 'Not recorded';
  const minutes = Math.floor(Math.max(0, now - timestamp) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
}

export function formatRunnerModel(value) {
  const model = safeRunnerText(value);
  return model ? MODEL_TIERS[model.toLowerCase()] || '' : '';
}

function recordRecency(record) {
  return Math.max(0, ...['lastHeartbeatAt', 'lastPollAt', 'lastCompleteAt', 'lastErrorAt']
    .map((key) => parseRunnerTimestamp(record[key]) || 0));
}

export function runnerRecordFor(status) {
  if (!isPlainObject(status) || !isPlainObject(status.runners)) return null;
  return Object.values(status.runners)
    .filter(isPlainObject)
    .reduce((latest, record) => (!latest || recordRecency(record) > recordRecency(latest) ? record : latest), null);
}

export function runnerHealthLabel(status, now = Date.now()) {
  const record = runnerRecordFor(status);
  if (!record) return { label: 'No runner heartbeat yet', tone: 'neutral' };

  const latest = recordRecency(record);
  const stale = !latest || now - latest > STALE_AFTER_MS;
  const errorAt = parseRunnerTimestamp(record.lastErrorAt);
  const completeAt = parseRunnerTimestamp(record.lastCompleteAt);
  const unresolvedError = errorAt !== null
    ? completeAt === null || errorAt >= completeAt
    : Boolean(safeRunnerText(record.lastErrorCode));
  const deadLetters = safeRunnerNumber(status.deadLetterCount);

  if (stale || unresolvedError || (deadLetters !== null && deadLetters > 0)) {
    return { label: 'Needs attention', tone: 'attention' };
  }

  const activeLogId = safeRunnerText(record.activeLogId);
  const expiresAt = parseRunnerTimestamp(record.expiresAt);
  if (activeLogId && expiresAt !== null && expiresAt > now) {
    return { label: 'Working', tone: 'working' };
  }

  const pollAt = parseRunnerTimestamp(record.lastPollAt);
  if (pollAt !== null && now - pollAt <= STALE_AFTER_MS) {
    return { label: 'Ready', tone: 'ready' };
  }

  return { label: 'Needs attention', tone: 'attention' };
}
